import { css } from "@compiled/react";

import StrokeWeightExtraLargeIcon from "@atlaskit/icon/core/stroke-weight-extra-large";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { getCurrRoute, useHeadingAtTop, toKebabCase } from "../../utils";

export interface SideNavLinkProps {
    type: "link";
    text: string;
    href: string;
    dataEventProps?: DataEventProps;
    anchors?: string[];
    depth?: number;
    isAnchorLink?: boolean;
    isAnchorAtTop?: boolean;
}

export const SideNavLink = ({
    text,
    href,
    dataEventProps,
    anchors,
    depth = 0,
    isAnchorLink = false,
    isAnchorAtTop = false
}: // eslint-disable-next-line complexity
SideNavLinkProps) => {
    const localizedHref = useLocalizedHref(href);
    const { headingIdAtTop, validAnchorIds } = useHeadingAtTop(anchors);

    const currRoute = getCurrRoute();
    const isActive = currRoute === href;

    const shouldShowIcon =
        (isActive && !anchors && !isAnchorLink) || isAnchorAtTop;
    const shouldBold = (isActive && !isAnchorLink) || isAnchorAtTop;

    // Filter anchors to only include those with corresponding H2s in body-content
    const validAnchors =
        anchors && isActive
            ? anchors.filter((anchor: string) => {
                  const anchorId = toKebabCase(anchor);
                  return validAnchorIds.has(anchorId);
              })
            : [];

    return (
        <li
            css={[
                listStyleNoneCss,
                listItemCss,
                depth === 0 && css({ paddingBlock: token("space.200") })
            ]}
        >
            <a
                href={localizedHref}
                {...dataEventProps}
                css={[
                    navLinkCss,
                    depth > 0 && subLevelLinkCss,
                    !isActive && !isAnchorAtTop && navLinkHoverCss
                ]}
            >
                <div css={dotContainerCss}>
                    {shouldShowIcon && <StrokeWeightExtraLargeIcon label="" />}
                </div>
                <span
                    className={depth === 0 || shouldBold ? "h6" : "body-md"}
                    style={{
                        fontFamily: wacToken("font.charlie.text")
                    }}
                >
                    {text}
                </span>
            </a>
            {validAnchors.length > 0 && (
                <ul
                    css={[
                        listStyleNoneCss,
                        css({
                            paddingInlineStart: token("space.400")
                        })
                    ]}
                >
                    {validAnchors.map((anchor: string) => {
                        const anchorId = toKebabCase(anchor);
                        const anchorIsAtTop = headingIdAtTop === anchorId;

                        return (
                            <SideNavLink
                                key={anchor}
                                dataEventProps={dataEventProps}
                                type="link"
                                href={`${localizedHref}#${anchorId}`}
                                text={anchor}
                                depth={depth + 1}
                                isAnchorLink={true}
                                isAnchorAtTop={anchorIsAtTop}
                            />
                        );
                    })}
                </ul>
            )}
        </li>
    );
};

const listStyleNoneCss = css({
    listStyle: "none",
    margin: token("space.0"),
    padding: token("space.0")
});

const listItemCss = css({
    display: "block"
});

const navLinkCss = css({
    display: "flex",
    alignItems: "center"
});

const navLinkHoverCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover > span": {
        color: wacToken("color.primary.blue")
    }
});

const subLevelLinkCss = css({
    paddingBlock: token("space.100")
});

const dotContainerCss = css({
    width: "24px",
    height: "24px",
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    marginInlineEnd: token("space.050"),
    alignSelf: "flex-start"
});
